import { useQuery } from '@tanstack/react-query'
import type { AppEntry, AppFile, AppsManifest } from './api'

/*
 * Desktop apps for download (docs/apps-downloads.md). Everything here is derived
 * from the manifest: a build that is not in it does not exist for the UI.
 */

export const MANIFEST_URL = '/downloads/manifest.json'
export const RELAY_STUDIO = 'relay-studio'
/** Relay Link (Strevix Bridge): the Windows app, the Mac menu bar app, and the Mac command line wsbridge (variant "cli"). */
export const RELAY_LINK = 'relay-link'
/** Anchor of the app Relay Link replaced; /apps#wsbridge links still land on Relay Link. */
export const OLD_ANCHORS: Record<string, string> = { wsbridge: RELAY_LINK }
/** Strevix for OBS: the OBS plugin (sign in, one-click setup, bridge built in), an installer per OS. */
export const STREVIX_OBS = 'strevix-obs'

/** Menu group subtitles when the manifest has none (docs/apps-downloads.md 3.3, 6). */
const TAGLINES: Record<string, string> = {
  [RELAY_STUDIO]: 'stream without OBS',
  [RELAY_LINK]: 'when your network blocks streaming ports',
  [STREVIX_OBS]: 'plugin for OBS Studio',
}

/** A command line build (wsbridge for Mac): no installer, run from Terminal. */
export const isCli = (f: AppFile) => f.variant === 'cli'

/** Where the user is: a computer we have builds for, another computer, or a phone (no downloads there). */
export type ClientOs = 'macos' | 'windows' | 'other' | 'phone'

interface UAData {
  platform?: string
  mobile?: boolean
}

export function detectOs(nav: Navigator = navigator): ClientOs {
  const ua = nav.userAgent ?? ''
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return 'phone'
  // iPadOS asks for the desktop site and reports a Mac.
  if (/Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1) return 'phone'
  const data = (nav as Navigator & { userAgentData?: UAData }).userAgentData
  if (data?.mobile) return 'phone'
  const platform = data?.platform || ua
  if (/Mac/i.test(platform)) return 'macos'
  if (/Win/i.test(platform)) return 'windows'
  return 'other'
}

/** Short OS name: "Relay Studio for Mac", "Mac · 94 MB". */
export const osShort = (os: string) => (os === 'macos' ? 'Mac' : os === 'windows' ? 'Windows' : os)
/** OS heading on /apps. */
export const osLong = (os: string) => (os === 'macos' ? 'macOS' : os === 'windows' ? 'Windows' : os)

/** Sizes as the OS file managers show them: 98566144 → "94 MB", 6082560 → "5.8 MB". */
export function formatSize(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  if (mb >= 10) return `${Math.round(mb)} MB`
  if (mb >= 0.1) return `${mb.toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** "2026-10-06T12:00:00Z" → "6 Oct 2026". */
export function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(d)
}

/** "macOS 11+, universal": the manifest's own text if it has one. */
export function requirement(f: AppFile): string {
  if (f.requirement) return f.requirement
  const arch = f.arch === 'amd64' || f.arch === 'x64' ? '64-bit' : f.arch === 'arm64' ? 'ARM' : f.arch
  return [f.min_os ? `${f.min_os}+` : osLong(f.os), arch].filter(Boolean).join(', ')
}

export const fileName = (f: AppFile) => decodeURIComponent(f.url.split('/').pop() ?? f.url)
export const kindLabel = (f: AppFile) => `.${f.kind || fileName(f).split('.').pop()}`

/** The user's files first, then the rest in manifest order. */
export function sortForOs(files: AppFile[], os: ClientOs): AppFile[] {
  // the user's OS first; within an OS the app with a window before a command line build
  return [...files].sort((a, b) => Number(b.os === os) - Number(a.os === os) || Number(isCli(a)) - Number(isCli(b)))
}

/** The file a user of this OS should take: the app with a window before a command line build. */
export function mainFile(app: AppEntry, os: string): AppFile | undefined {
  return app.files.find((f) => f.os === os && !isCli(f)) ?? app.files.find((f) => f.os === os)
}

/**
 * The test id part of a file: "<app>-<os>", and "-cli" for a command line build
 * that sits next to an app for the same OS (Strevix Bridge on a Mac).
 */
export function fileKey(app: AppEntry, f: AppFile): string {
  const beside = isCli(f) && app.files.some((g) => g.os === f.os && !isCli(g))
  return `${app.id}-${f.os}${beside ? '-cli' : ''}`
}

class ManifestError extends Error {}

/** Keeps only what the UI can show: apps with at least one complete file. */
function normalize(raw: unknown): AppsManifest {
  const m = raw as Partial<AppsManifest> | null
  if (!m || typeof m !== 'object' || !Array.isArray(m.apps)) throw new ManifestError('Malformed list of apps')
  const apps: AppEntry[] = []
  for (const a of m.apps) {
    if (!a || typeof a.id !== 'string' || typeof a.name !== 'string' || !Array.isArray(a.files)) continue
    const files = a.files.filter(
      (f): f is AppFile => !!f && typeof f.url === 'string' && !!f.url && typeof f.os === 'string' && typeof f.size === 'number',
    )
    if (files.length) apps.push({ ...a, summary: a.summary ?? '', tagline: a.tagline ?? TAGLINES[a.id], files })
  }
  return { schema: m.schema ?? 1, version: String(m.version ?? ''), released_at: String(m.released_at ?? ''), notes_url: m.notes_url, apps }
}

/** null: nothing published (404, or a page instead of JSON from an SPA fallback). */
async function fetchManifest(): Promise<AppsManifest | null> {
  const r = await fetch(MANIFEST_URL, { headers: { Accept: 'application/json' }, credentials: 'omit', cache: 'no-cache' })
  if (r.status === 404) return null
  if (!r.ok) throw new ManifestError(`HTTP ${r.status}`)
  if (!/json/i.test(r.headers.get('content-type') ?? '')) return null
  return normalize(await r.json())
}

export function useManifest() {
  return useQuery({
    queryKey: ['apps-manifest'],
    queryFn: fetchManifest,
    staleTime: 5 * 60_000,
    // A 404 is an answer (null), not an error, so it is never retried.
    retry: 1,
    retryDelay: 1000,
  })
}

export type AppsState = 'loading' | 'error' | 'none' | 'ready'

export interface AppsView {
  state: AppsState
  manifest?: AppsManifest
  /** apps with files, manifest order */
  apps: AppEntry[]
  os: ClientOs
  app: (id: string) => AppEntry | undefined
  /** a new request is in flight (Try again) */
  fetching: boolean
  retry: () => void
}

/** The manifest as the UI sees it. `state` is 'none' when there is nothing to show at all. */
export function useApps(): AppsView {
  const q = useManifest()
  const apps = q.data?.apps ?? []
  const state: AppsState = q.isPending ? 'loading' : q.isError && !q.data ? 'error' : apps.length ? 'ready' : 'none'
  return {
    state,
    manifest: q.data ?? undefined,
    apps,
    os: detectOs(),
    app: (id) => apps.find((a) => a.id === id),
    fetching: q.isFetching,
    retry: () => void q.refetch(),
  }
}

/**
 * Buttons for one app in a hint: the user's build as the primary one; without it
 * every published build as secondary ones; none on a phone or an unknown OS.
 */
export function hintFiles(app: AppEntry | undefined, os: ClientOs): { mine?: AppFile; others: AppFile[] } {
  if (!app) return { others: [] }
  const mine = mainFile(app, os)
  return { mine, others: app.files.filter((f) => f !== mine) }
}

/** OS names that have at least one file, for sentences like "run on Mac and Windows". */
export function publishedOs(apps: AppEntry[]): string[] {
  const set: string[] = []
  for (const a of apps) for (const f of a.files) if (!set.includes(f.os)) set.push(f.os)
  return set
}

export function joinAnd(items: string[]): string {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}
